import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import { authService } from "../../services/authService";

export default function ResetPasswordForm() {
    const location = useLocation();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token, setToken] = useState("");
    const [formData, setFormData] = useState({
        password: "",
        confirmPassword: ""
    });
    const [errors, setErrors] = useState({
        password: "",
        confirmPassword: "",
    });
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState("");
    const [resetSuccess, setResetSuccess] = useState(false);
    const [invalidToken, setInvalidToken] = useState(false);

    // Extrair token da URL quando o componente for montado
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tokenFromUrl = params.get("token");
        if (tokenFromUrl) {
            setToken(tokenFromUrl);
        } else {
            setInvalidToken(true);
        }
    }, [location]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Limpar erros quando o usuário digita
        if (errors[name as keyof typeof errors]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }

        // Limpar erro da API quando o usuário faz alguma alteração
        if (apiError) {
            setApiError("");
        }
    };

    const validate = () => {
        const newErrors = {
            password: "",
            confirmPassword: "",
        };
        let isValid = true;

        if (!token) {
            setInvalidToken(true);
            isValid = false;
            return isValid;
        }

        if (!formData.password) {
            newErrors.password = "Senha é obrigatória";
            isValid = false;
        } else if (formData.password.length < 6) {
            newErrors.password = "A senha deve ter pelo menos 6 caracteres";
            isValid = false;
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = "Confirmação de senha é obrigatória";
            isValid = false;
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "As senhas não coincidem";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);
        setApiError("");

        try {
            const response = await authService.resetPassword(token, formData.password);

            if (response.success) {
                setResetSuccess(true);
            } else {
                if (response.message?.toLowerCase().includes("expirado") || 
                    response.message?.toLowerCase().includes("inválido")) {
                    setInvalidToken(true);
                } else {
                    setApiError(response.message || "Não foi possível redefinir sua senha. Tente novamente.");
                }
            }
        } catch (error: any) {
            console.error("Erro ao redefinir senha:", error);

            if (error.response && error.response.data && error.response.data.message) {
                const errorMessage = error.response.data.message;
                if (errorMessage.toLowerCase().includes("expirado") || 
                    errorMessage.toLowerCase().includes("inválido")) {
                    setInvalidToken(true);
                } else {
                    setApiError(errorMessage);
                }
            } else {
                setApiError("Ocorreu um erro ao redefinir sua senha. Por favor, tente novamente.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRequestNewLink = () => {
        navigate("/forget-password");
    };

    if (invalidToken) {
        return (
            <div className="flex flex-col flex-1">
                <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                    <div className="text-center">
                        <h1 className="mb-4 font-semibold text-gray-800 text-title-lg dark:text-white/90">
                            Link Inválido
                        </h1>
                        <p className="mb-8 text-base text-gray-500 dark:text-gray-400">
                            O link de redefinição de senha é inválido ou expirou. Por favor, solicite um novo link de redefinição de senha.
                        </p>

                        <button
                            onClick={handleRequestNewLink}
                            className="flex items-center justify-center w-full px-4 py-3 mb-6 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                        >
                            Solicitar novo link
                        </button>

                        <Link
                            to="/signin"
                            className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 hover:text-brand-500 dark:hover:text-brand-400"
                        >
                            <svg
                                className="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 19l-7-7 7-7"
                                ></path>
                            </svg>
                            Voltar para o login
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1">
            <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                <div>
                    <div className="mb-5 sm:mb-8">
                        <img
                            src="/images/logo/logo-icon.svg"
                            alt="Logo"
                            className="mx-auto"
                            width={48}
                            height={48}
                        />
                        <h1 className="mt-6 mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                            Redefinir senha
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {resetSuccess
                                ? "Sua senha foi redefinida com sucesso!"
                                : "Digite sua nova senha abaixo para redefinir seu acesso."}
                        </p>
                    </div>
                    <div>
                        {resetSuccess ? (
                            <div className="text-center">
                                <div className="mb-6 p-4 bg-success-100 text-success-700 rounded-md dark:bg-success-900/30 dark:text-success-500">
                                    <p>Sua senha foi redefinida com sucesso!</p>
                                </div>
                                <p className="mb-6 text-gray-600 dark:text-gray-400">
                                    Agora você pode fazer login com sua nova senha.
                                </p>
                                <Link
                                    to="/signin"
                                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                                >
                                    Ir para Login
                                </Link>
                            </div>
                        ) : (
                            <>
                                {apiError && (
                                    <div className="p-3 mb-5 text-sm text-white rounded-lg bg-error-500">
                                        {apiError}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className="space-y-6">
                                        <div>
                                            <Label>
                                                Nova senha <span className="text-error-500">*</span>{" "}
                                            </Label>
                                            <div className="relative">
                                                <Input
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    type={showPassword ? "text" : "password"}
                                                    placeholder="Digite sua nova senha"
                                                    className={errors.password ? "border-error-500" : ""}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                                                    tabIndex={-1}
                                                >
                                                    {showPassword ? <EyeCloseIcon /> : <EyeIcon />}
                                                </button>
                                            </div>
                                            {errors.password && (
                                                <p className="mt-1 text-sm text-error-500">{errors.password}</p>
                                            )}
                                        </div>
                                        <div>
                                            <Label>
                                                Confirme a nova senha <span className="text-error-500">*</span>{" "}
                                            </Label>
                                            <div className="relative">
                                                <Input
                                                    name="confirmPassword"
                                                    value={formData.confirmPassword}
                                                    onChange={handleChange}
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    placeholder="Confirme sua nova senha"
                                                    className={errors.confirmPassword ? "border-error-500" : ""}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                                                    tabIndex={-1}
                                                >
                                                    {showConfirmPassword ? <EyeCloseIcon /> : <EyeIcon />}
                                                </button>
                                            </div>
                                            {errors.confirmPassword && (
                                                <p className="mt-1 text-sm text-error-500">{errors.confirmPassword}</p>
                                            )}
                                        </div>
                                        <div>
                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600 disabled:opacity-70 disabled:cursor-not-allowed"
                                            >
                                                {isLoading ? "Processando..." : "Redefinir senha"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                                <div className="mt-5 text-center">
                                    <div className="text-sm font-normal text-gray-700 dark:text-gray-400">
                                        <Link
                                            to="/signin"
                                            className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                                        >
                                            Voltar para Login
                                        </Link>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
